// src/contexts/NotificationContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import useAuthStore from '../store/authStore';

const NotificationContext = createContext();

export const useNotificationContext = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotificationContext debe ser usado dentro de NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user } = useAuthStore();
    const [selectedMarca, setSelectedMarca] = useState(null);
    const [notificationConfig, setNotificationConfig] = useState({});
    const [templates, setTemplates] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const {
        isSupported,
        isEnabled,
        fcmToken,
        loading: notificationsLoading,
        error,
        requestPermission,
        sendNotification,
        getNotificationConfig,
        updateNotificationConfig,
        getNotificationTemplates,
        createNotificationTemplate,
        updateNotificationTemplate,
        getNotificationHistory,
        retryNotification,
        getDeliveryStatus
    } = useNotifications();

    const loadNotificationConfig = useCallback(async (marcaId) => {
        if (!marcaId) return;

        try {
            setLoading(true);
            const config = await getNotificationConfig(marcaId);
            setNotificationConfig(config);
        } catch (error) {
            console.error('Error cargando configuración de notificaciones:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadTemplates = useCallback(async () => {
        if (!selectedMarca) return;

        try {
            setLoading(true);
            const templatesData = await getNotificationTemplates();
            setTemplates(templatesData);
        } catch (error) {
            console.error('Error cargando templates:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedMarca]);

    useEffect(() => {
        if (selectedMarca) {
            loadNotificationConfig(selectedMarca);
        }
    }, [selectedMarca, loadNotificationConfig]);

    useEffect(() => {
        if (selectedMarca) {
            loadTemplates();
        }
    }, [selectedMarca, loadTemplates]);

    // Actualizar configuración
    const updateConfig = async (marcaId, newConfig) => {
        try {
            await updateNotificationConfig(marcaId, newConfig);
            setNotificationConfig(newConfig);
            return true;
        } catch (error) {
            console.error('Error actualizando configuración:', error);
            return false;
        }
    };

    // Crear nuevo template
    const createTemplate = async (template) => {
        try {
            const newTemplate = await createNotificationTemplate(template);
            setTemplates(prev => [...prev, newTemplate]);
            return newTemplate;
        } catch (error) {
            console.error('Error creando template:', error);
            throw error;
        }
    };

    // Actualizar template existente
    const updateTemplate = async (templateId, updatedTemplate) => {
        try {
            const updated = await updateNotificationTemplate(templateId, updatedTemplate);
            setTemplates(prev =>
                prev.map(template =>
                    template._id === templateId ? updated : template
                )
            );
            return updated;
        } catch (error) {
            console.error('Error actualizando template:', error);
            throw error;
        }
    };

    // Enviar notificación con contexto
    const sendNotificationWithContext = async (notificationData) => {
        try {
            const dataWithContext = {
                ...notificationData,
                marcaId: selectedMarca,
                userId: user?.userId
            };

            const result = await sendNotification(dataWithContext);

            // Actualizar contador de no leídas si es necesario
            if (notificationData.channel === 'push') {
                setUnreadCount(prev => prev + 1);
            }

            return result;
        } catch (error) {
            console.error('Error enviando notificación:', error);
            throw error;
        }
    };

    // Obtener historial con contexto
    const getHistoryWithContext = async (filters = {}) => {
        if (!selectedMarca) {
            throw new Error('No hay marca seleccionada');
        }

        return await getNotificationHistory(selectedMarca, filters);
    };

    // Reenviar notificación con contexto
    const retryNotificationWithContext = async (messageId) => {
        try {
            const result = await retryNotification(messageId);

            // Actualizar contador si es necesario
            setUnreadCount(prev => Math.max(0, prev - 1));

            return result;
        } catch (error) {
            console.error('Error reenviando notificación:', error);
            throw error;
        }
    };

    // Marcar notificaciones como leídas
    const markAsRead = () => {
        setUnreadCount(0);
    };

    // Verificar si un canal está habilitado
    const isChannelEnabled = (channel) => {
        return notificationConfig[channel]?.enabled || false;
    };

    // Obtener configuración de un canal específico
    const getChannelConfig = (channel) => {
        return notificationConfig[channel] || {};
    };

    const value = {
        // Estado
        selectedMarca,
        notificationConfig,
        templates,
        unreadCount,
        isSupported,
        isEnabled,
        fcmToken,
        loading,
        error,

        // Métodos de configuración
        setSelectedMarca,
        updateConfig,
        loadNotificationConfig,
        isChannelEnabled,
        getChannelConfig,

        // Métodos de templates
        createTemplate,
        updateTemplate,
        loadTemplates,

        // Métodos de notificaciones
        requestPermission,
        sendNotification: sendNotificationWithContext,
        getHistory: getHistoryWithContext,
        retryNotification: retryNotificationWithContext,
        getDeliveryStatus,

        // Utilidades
        markAsRead
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}; 